import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';
import { Badge, Card, EmptyState, QueryBoundary, StatCard } from '../../../components/ui/index.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonReads } from './daemon-queries.js';
import type { ReflectionEntry } from '../../../types/daemon.js';
import type { DaemonRecord } from '../../../generated/api/types.gen.js';

interface DaemonReflectionsTabProps {
  daemon: DaemonRecord;
}

const TREND_DISPLAY: Record<
  string,
  { icon: React.ReactNode; variant: 'success' | 'danger' | 'neutral'; label: string }
> = {
  improving: { icon: <TrendingUp size={14} />, variant: 'success', label: 'Improving' },
  declining: { icon: <TrendingDown size={14} />, variant: 'danger', label: 'Declining' },
  stable: { icon: <Minus size={14} />, variant: 'neutral', label: 'Stable' },
};

export const DaemonReflectionsTab: React.FC<DaemonReflectionsTabProps> = ({ daemon }) => {
  const { scope } = useDaemonScope();
  const reads = daemonReads(scope);

  const { data: trend } = useQuery(reads.trend(daemon.id));
  const reflectionsQuery = useQuery(reads.reflections(daemon.id));

  const trendInfo = TREND_DISPLAY[trend?.trend ?? 'stable'];

  return (
    <QueryBoundary
      query={reflectionsQuery}
      label="reflections"
      loading={<div className="text-muted text-sm p-4">Loading reflections...</div>}
    >
      {(reflectionsData) => {
        const reflections: ReflectionEntry[] = reflectionsData?.data ?? [];
        return (
          <div className="space-y-6">
            {/* Trend summary */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                label="Average Quality"
                value={trend ? `${(trend.avgQuality * 100).toFixed(0)}%` : '—'}
                icon={<Activity size={16} />}
              />
              <StatCard label="Trend" value={trendInfo.label} icon={trendInfo.icon} />
              <StatCard label="Reflections" value={reflections.length} icon={<Activity size={16} />} />
            </div>

            {/* Quality trend visualization */}
            {reflections.length > 0 && (
              <Card title="Quality over time">
                <div className="flex items-end gap-1 h-24">
                  {reflections.slice(-30).map((r, i) => {
                    const height = Math.max(4, (r.quality_rating ?? 0) * 100);
                    const isGood = (r.quality_rating ?? 0) >= 0.5;
                    return (
                      <div
                        key={i}
                        className={`flex-1 rounded-t ${isGood ? 'bg-success' : 'bg-danger'} opacity-70 hover:opacity-100 transition`}
                        style={{ height: `${height}%` }}
                        title={`Quality: ${((r.quality_rating ?? 0) * 100).toFixed(0)}% — ${r.timestamp}`}
                      />
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Reflection entries */}
            {reflections.length === 0 ? (
              <Card>
                <EmptyState
                  icon={Activity}
                  title="No reflections yet"
                  description="Run it once to generate reflection data."
                />
              </Card>
            ) : (
              <div className="space-y-3">
                {reflections
                  .slice()
                  .reverse()
                  .map((r) => (
                    <Card key={`${r.runId}-${r.timestamp}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={trendInfo.variant}>Q: {((r.quality_rating ?? 0) * 100).toFixed(0)}%</Badge>
                          <span className="text-xs text-muted">Run {r.runId?.slice(0, 8) ?? 'unknown'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted">
                          <span>{r.findings_count ?? 0} findings</span>
                          <span>{r.duration_ms ? `${Math.round(r.duration_ms / 1000)}s` : '—'}</span>
                          <span>{r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1.5 text-sm">
                        {r.what_worked && r.what_worked.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-success font-medium text-xs min-w-[60px]">Worked:</span>
                            <span className="text-body">{r.what_worked.join('; ')}</span>
                          </div>
                        )}
                        {r.what_failed && r.what_failed.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-danger font-medium text-xs min-w-[60px]">Failed:</span>
                            <span className="text-body">{r.what_failed.join('; ')}</span>
                          </div>
                        )}
                        {r.next_time && r.next_time.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-warning font-medium text-xs min-w-[60px]">Next:</span>
                            <span className="text-body">{r.next_time.join('; ')}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
              </div>
            )}
          </div>
        );
      }}
    </QueryBoundary>
  );
};
